import type {
	RealtimePostgresChangesPayload,
	RealtimePostgresDeletePayload,
	RealtimePostgresUpdatePayload,
	SupabaseClient
} from '@supabase/supabase-js'
import { supabase } from "@/lib/supabase/service-client"

import { logError } from '@/utils/errorLogging'
import { Database } from '@/supabase/types'
import { SubscribeStatusError } from '@/realtime/subscribe_status_error'
import { IsomorphicLogger } from '@/utils/logger/types'
import { createClientLogger } from '@/utils/logger/clientLogger'
import { printObject } from '../utils'

type SubscriptionState = 'INITIALIZING' | 'SUBSCRIBED' | 'ERROR' | 'UNSUBSCRIBED'
type BasePayload = { [key: string]: any }

export type HandlerConfig<TPayload extends BasePayload = BasePayload> = {
  key: string
  channelName: string
  schema: string
  table: string
  filter?: string
  inactivityThreshold: number
  events: Array<'INSERT' | 'UPDATE' | 'DELETE' | '*'>
  handler: (payload: RealtimePostgresChangesPayload<TPayload>) => Promise<void> | void
}

type SubscriptionInfo<TPayload extends BasePayload = BasePayload> = {
  config: HandlerConfig<TPayload>
  channel: ReturnType<typeof supabase.channel> | null
  lastEventTime: number
  state: SubscriptionState
  lastError?: string
	token: number // timestamp
}

class ExponentialBackoff {
  private _attempts = 0
  constructor(
    private maxAttempts = 10,
    private baseDelay = 5_000,
    private maxDelay = 300_000
  ) {}

	get attempts(): number {
    return this._attempts
  }

  async wait(): Promise<boolean> {
    if (this._attempts >= this.maxAttempts) return false
    const delay = Math.min(this.baseDelay * Math.pow(2, this._attempts), this.maxDelay)
    await new Promise(resolve => setTimeout(resolve, delay))
    this._attempts++
    return true
  }

  reset(): void {
    this._attempts = 0
  }
}

type SubHealthStatus<TPayload extends BasePayload = BasePayload> = Omit<SubscriptionInfo<TPayload>, 'channel' | 'config'>

export class BaseSubscriptionManager<TPayload extends BasePayload = BasePayload> {
  protected supabase: SupabaseClient<Database>
	protected subscriptions: Record<string, SubscriptionInfo<any>> = {}
  private isInitialized: boolean = false
  private isInitializing: boolean = false

  private lastResubscribeAttempt: number = 0
  private readonly RESUBSCRIBE_COOLDOWN = 55_000 // 55 seconds (a bit below the 1 minute call interval to account for small clock timing issues or busy thread)
  private circuitBreakerTripped: boolean = false
  private readonly CIRCUIT_BREAKER_RESET_TIMEOUT = 300_000 // 5 minutes
  private reconnectionInterval: NodeJS.Timeout | null = null
	protected logger:IsomorphicLogger

  constructor(supabase: SupabaseClient, namespace: string) {
    this.supabase = supabase
		this.logger = createClientLogger(namespace, supabase)
  }

  async registerHandler(config: HandlerConfig<TPayload>) {
    if (this.subscriptions[config.key]) {
			this.logger.warn(`Attempting to register already existing handler ${config.key}`)
			return
		}
		const token = Date.now()
    this.subscriptions[config.key] = {
      config,
      channel: null as any,
      lastEventTime: Date.now(),
      state: 'INITIALIZING',
			token
    }
    if (this.isInitialized) {
      await this.subscribe(config.key)
    }
  }

  async unregisterHandler(key: string) {
		const sub = this.subscriptions[key]
		if (sub) {
			await this.unsubscribe(key, sub.token)
		}
  }

	public isCircuitBreakerTripped(): boolean {
		return this.circuitBreakerTripped
	}

	async ensureInitialized(): Promise<void> {
		if (!this.isInitialized) {
			try {
				await this.initialize()
			}
			catch (error) {
				logError(this.logger, error, 'Error during initialization:')
				this.isInitializing = false
				await this.cleanup()
				throw error
			}
		}
	}

	async initialize(): Promise<void> {
		if (this.isInitialized || this.isInitializing) return
		this.isInitializing = true
		this.logger.info('Initializing subscription manager')

		if (!(await this.checkSupabaseConnection())) {
			this.logger.error('Unable to connect to Supabase, delaying initialization')
      await new Promise(resolve => setTimeout(resolve, 30000)) // Wait 30 seconds before retrying
			this.isInitializing = false
      return this.initialize() // Recursive call to retry initialization
		}

		try {
			// Only subscribe those that are not already SUBSCRIBED
			const subsToInit = Object.values(this.subscriptions)
				.filter(sub => sub.state !== 'SUBSCRIBED')

			const results = await Promise.all(subsToInit.map(sub => this.subscribeWithRetry(sub)))

			// Log any non-empty results (which would be error messages)
			results.forEach((result, index) => {
				if (result) {
					this.logger.error(`Initialization error for subscription ${subsToInit[index].config.key}: ${result}`)
				}
			})

			if (results.length > 0 && results.every(result => result)) {
				throw new Error('All subscriptions failed to initialize.')
			}
			this.isInitialized = true
			this.isInitializing = false
		}
		catch (error) {
			logError(this.logger, error, 'Error during initialization:')
			this.isInitializing = false
			throw error
		}
	}

  private async subscribeWithRetry(sub: SubscriptionInfo<TPayload>): Promise<string> {
    const backoff = new ExponentialBackoff()
    while (await backoff.wait()) {
      try {
				this.logger.info(`Starting Subscribing to ${sub.config.key}`)
        if (sub.channel) {
          await sub.channel.unsubscribe()
        }
        const channel = this.supabase.channel(sub.config.channelName)

				for (const event of sub.config.events) {
					if (event === 'INSERT') {
						channel.on('postgres_changes', {
							event: 'INSERT',
							schema: sub.config.schema,
							table: sub.config.table,
							filter: sub.config.filter
						}, async (payload) => {
							sub.lastEventTime = Date.now()
							await sub.config.handler(payload as RealtimePostgresChangesPayload<TPayload>)
						})
					}
					else if (event === 'DELETE') {
						channel.on('postgres_changes', {
							event: 'DELETE',
							schema: sub.config.schema,
							table: sub.config.table,
							filter: sub.config.filter
						}, async (payload) => {
							sub.lastEventTime = Date.now()
							await sub.config.handler(payload as RealtimePostgresDeletePayload<TPayload>)
						})
					}
					else if (event === 'UPDATE') {
						channel.on('postgres_changes', {
							event: 'UPDATE',
							schema: sub.config.schema,
							table: sub.config.table,
							filter: sub.config.filter
						}, async (payload) =>  {
							sub.lastEventTime = Date.now()
							await sub.config.handler(payload as RealtimePostgresUpdatePayload<TPayload>)
						})
					}
				}

				await Promise.race([
					new Promise<void>((resolve, reject) => {
						channel.subscribe((status, error) => {
							if (error) reject(error)
							else if (status === 'SUBSCRIBED') resolve()
						})
					}),
					new Promise<void>((_, reject) =>
						setTimeout(() => reject(new Error('Subscribe timed out')), 5000)
					)
				])

				sub.channel = channel
				sub.lastEventTime = Date.now()
				sub.state = 'SUBSCRIBED'
				sub.lastError = undefined // clear previous error if needed

				this.logger.debug(`Done Subscribing to ${sub.config.key}`)
        backoff.reset()
        return ''
      }
			catch (error) {
				const errorMessage = error instanceof Error ? error.message : String(error)
				if (error instanceof SubscribeStatusError) {
					this.logger.error(`Error subscribing to ${sub.config.key} (attempt ${backoff.attempts}) errorMessage=${error.message}`)
				}
				else {
					logError(this.logger, error, `Error subscribing to ${sub.config.key} (attempt ${backoff.attempts})`)
				}

				// sub.channel = null   can't make null since it breaks the unsubscribe which should delete the subscription
				sub.lastEventTime = Date.now()
				sub.state = 'ERROR'
				sub.lastError = errorMessage
        await this.unsubscribe(sub.config.key)

				if (!(await this.checkSupabaseConnection())) {
					this.logger.error('Supabase connection lost, aborting subscription attempts')
					return 'Supabase connection lost'
				}
      }
    }
    return 'Max retry attempts reached for ChatCompletion subscription'
  }

  async unsubscribe(key?: string, token?: number): Promise<void> {
    const cleanupSubscription = async (subKey: string, token?: number) => {
	    const sub = this.subscriptions[subKey]
			if (sub) {
    		// Only proceed if the token matches (or not provided)
    		if (token === undefined || sub.token <= token) {
					if (sub?.channel) {
						try {
							await sub.channel.unsubscribe()
							this.logger.info(`Unsubscribed from ${subKey} channel`)
						}
						catch (error) {
							logError(this.logger, error, `Error unsubscribing from ${subKey} channel:`)
						}
					}
					else {
						this.logger.warn(`No valid channel to unsubscribe for: ${subKey} / ${printObject(sub)}`)
					}
					delete this.subscriptions[subKey]
				}
			}
			else {
				this.logger.error(`Unsubscribe found no subscription for: ${subKey}`)
			}
    }

    if (key) {
      await cleanupSubscription(key, token)
    }
		else {
			this.logger.info(`Unsubscribing from all`)
      await Promise.all(Object.keys(this.subscriptions).map(cleanupSubscription))
    	this.isInitialized = false
    }
	}

	/**
		This method checks the health of the subscriptions in the manager. While it can be called to return a
		health status, normally you should instead use checkSubscriptions() which will call checkHealth and do a
		more complete job.
	*/
  async checkHealth(): Promise<{ status: string, reason?: string } | Record<string, SubHealthStatus<TPayload>>> {
		const subSz = Object.keys(this.subscriptions).length
  	this.logger.info(`Starting health check (${subSz} subscriptions) ...`)

		if (this.circuitBreakerTripped) {
			this.logger.warn('Circuit breaker tripped, skipping health check')
			return { status: 'unhealthy', reason: 'Circuit breaker tripped' }
		}

		if (!(await this.checkSupabaseConnection())) {
			this.logger.warn('Supabase connection check failed, reporting unhealthy status')
			return { status: 'unhealthy', reason: 'Supabase connection failed' }
		}

    const now = Date.now()
    const status: Record<string, SubHealthStatus<TPayload>> = {}

    for (const key in this.subscriptions) {
      const sub = this.subscriptions[key]
			const timeSinceLast = now - sub.lastEventTime
      status[key] = {
        state: sub.state,
        lastEventTime: sub.lastEventTime,
        lastError: sub.lastError,
				token: sub.token
      }

      if (timeSinceLast > sub.config.inactivityThreshold) {
				this.logger.warn(`No events received for ${key} in the last ${sub.config.inactivityThreshold / 1000} seconds` +
						`(Last event: ${new Date(sub.lastEventTime).toISOString()})`)
        status[key].state = 'ERROR'
        status[key].lastError = 'Inactivity threshold exceeded'
      }
			else {
				this.logger.debug(`Subscription ${key} is active. Last event: ${new Date(sub.lastEventTime).toISOString()}`);
			}
    }

	  this.logger.debug('Health check completed.');
		return status
  }

  async cleanup(): Promise<void> {
    await this.unsubscribe()  //will do all subscriptions
    this.isInitialized = false
  }

	/**
		This method performs a subscription status check. It is the method to normally call at interval to make
		sure that the manager is running correctly. It will internally call checkHealth() so you should never do
		both.
	*/
	async checkSubscriptions(): Promise<void> {
	  this.logger.info('Periodic subscription status check...');

  	if (this.circuitBreakerTripped) {
    	this.logger.warn('Circuit breaker tripped, skipping resubscription')
      return
    }
    if (!(await this.checkSupabaseConnection())) {
      this.logger.warn('Supabase connection unavailable, starting reconnection attempts')
      this.startReconnectionAttempts()
      return
    }

    const now = Date.now()
    if (now - this.lastResubscribeAttempt < this.RESUBSCRIBE_COOLDOWN) {
    	this.logger.warn(`Resubscribe attempt too soon, skipping. Time since last attempt: ${now - this.lastResubscribeAttempt}ms`)
      return
    }

    this.lastResubscribeAttempt = now
  	const healthStatus = await this.checkHealth()
  	if ('reason' in healthStatus) {
    	this.logger.warn(`Health check failed: ${healthStatus.reason}`)
    	return
  	}
    const resubscribePromises: Promise<string>[] = []

  	for (const [key, info] of Object.entries(healthStatus)) {
      if (info.state === 'ERROR') {
      	this.logger.info(`Need to resubscribe to ${key}, message: ${info.lastError}`)
        resubscribePromises.push(this.subscribe(key))
      }
    }

    if (resubscribePromises.length > 0) {
      this.logger.info(`Awaiting resubscription to ${resubscribePromises.length} channel(s)`)
      const results = await Promise.allSettled(resubscribePromises)
      let allFailed = true

			for (let i = 0; i < results.length; i++) {
				const result = results[i]
				if (result.status === 'fulfilled') {
					const resultValue = result.value === '' ? 'success' : result.value
					const key = Object.keys(healthStatus)[i]
					this.logger.info(`Resubscription result for handler ${key}: ${resultValue}`)
					// If resubscription was successful, update the subscription state
					if (resultValue === 'success') {
						allFailed = false
						if (this.subscriptions[key]) {
							this.subscriptions[key].state = 'SUBSCRIBED'
							this.subscriptions[key].lastEventTime = Date.now()
							this.subscriptions[key].lastError = undefined
						}
					}
				}
				else {
					this.logger.error(`Resubscription failed for channel ${i}: ${result.reason}`)
				}
			}
			if (allFailed) {
				this.logger.warn('All resubscription attempts failed, tripping circuit breaker')
				this.tripCircuitBreaker()
			}
  	}
		else {
    	this.logger.debug('No resubscription needed.');
    }
  	this.logger.debug('checkSubscriptions completed.');
  }

  private startReconnectionAttempts(): void {
    if (this.reconnectionInterval) return

    this.reconnectionInterval = setInterval(async () => {
      if (await this.checkSupabaseConnection()) {
        this.logger.info('Supabase connection restored, attempting to reinitialize')
        clearInterval(this.reconnectionInterval!)
        this.reconnectionInterval = null
        await this.initialize()
      }
			else {
        this.logger.warn('Supabase connection still unavailable')
      }
    }, 60000) // Check every minute
	}

  private async subscribe(key: string): Promise<string> {
    const sub = this.subscriptions[key]
    if (!sub) return `Unknown subscription key: ${key}`
    return this.subscribeWithRetry(sub)
  }

  private tripCircuitBreaker(): void {
    this.circuitBreakerTripped = true
    setTimeout(() => {
      this.circuitBreakerTripped = false
      this.logger.info('Circuit breaker reset')
    }, this.CIRCUIT_BREAKER_RESET_TIMEOUT)
  }

  private async checkSupabaseConnection(): Promise<boolean> {
		return true
    // try {
    //   const { data, error } = await supabase.rpc('heartbeat')
    //   return !error && data === true
    // } catch (e) {
    //   subMgrLogger.error('Error checking Supabase connection:', e)
    //   return false
    // }
  }
}